import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

/** Renders one sheet with PDF.js and hands children a matrix from sheet space (PyMuPDF points,
 *  origin top-left, unrotated) to CSS pixels — the SVG overlay draws in sheet space and never
 *  cares about zoom or rotation. Pinch = zoom the drawing, two fingers = pan, pen always draws.
 *
 *  DRW-FIX-2 — the geometry (canvas CSS size, overlay matrix, hit-testing) is set SYNCHRONOUSLY
 *  when zoom changes; only the bitmap re-render is debounced. That keeps the overlay locked to the
 *  paper at every zoom, lets the cursor anchor be applied against a scroll range that already
 *  exists, and stops the wheel from queueing full-resolution renders it will throw away. */
/** Low-res raster compare of two pages → changed cells in sheet space (points, top-left origin).
 *  The title-block strip (right 15%) is skipped — its revision stamp changes on every reissue. */
async function diffPages(pageA, pageB, W = 240, cols = 30) {
  const base = pageA.getViewport({ scale: 1, rotation: 0 });
  const scale = W / base.width;
  const draw = async (page) => {
    const vp = page.getViewport({ scale, rotation: 0 });
    const c = document.createElement("canvas"); c.width = Math.ceil(vp.width); c.height = Math.ceil(vp.height);
    const ctx = c.getContext("2d", { willReadFrequently: true });
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
    await page.render({ canvasContext: ctx, viewport: vp }).promise;
    return ctx.getImageData(0, 0, c.width, c.height);
  };
  const [a, b] = await Promise.all([draw(pageA), draw(pageB)]);
  const w = Math.min(a.width, b.width), h = Math.min(a.height, b.height);
  const rows = Math.max(1, Math.round(cols * h / w));
  const cw = w / cols, ch = h / rows, out = [];
  for (let j = 0; j < rows; j += 1) for (let i = 0; i < cols; i += 1) {
    if (i / cols > 0.85) continue;
    let hot = 0, n = 0;
    for (let y = Math.floor(j * ch); y < Math.floor((j + 1) * ch); y += 1) for (let x = Math.floor(i * cw); x < Math.floor((i + 1) * cw); x += 1) {
      const pa = (y * a.width + x) * 4, pb = (y * b.width + x) * 4;
      const d = Math.abs(a.data[pa] - b.data[pb]) + Math.abs(a.data[pa + 1] - b.data[pb + 1]) + Math.abs(a.data[pa + 2] - b.data[pb + 2]);
      if (d > 90) hot += 1;
      n += 1;
    }
    if (n && hot / n > 0.015) out.push([i * base.width / cols, j * base.height / rows, base.width / cols, base.height / rows]);
  }
  return out;
}

/** Old linework in blue on a transparent ground: paper goes clear, ink goes blue, so the opacity
 *  slider fades only the drawing and never washes the current sheet. */
function blueprint(ctx, w, h) {
  if (!w || !h) return;
  const img = ctx.getImageData(0, 0, w, h), d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const lum = (d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114);
    d[i] = 29; d[i + 1] = 78; d[i + 2] = 216; d[i + 3] = Math.max(0, 255 - lum);
  }
  ctx.putImageData(img, 0, 0);
}

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 8;
const clampZoom = (z) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));

/** sheet space → css px inside the stage, for anyone outside this file that needs to aim the
 *  scroller at a point on the paper (`frame` comes back with onReady). */
export const sheetToCss = ([x, y], f) => {
  if (!f?.vp) return [0, 0];
  return f.vp.convertToViewportPoint(x, f.sheetH - y);
};

export default function PdfStage({ pdfBytes, height, zoom, rotation, onZoom, tool, onPointer, children,
                                   containerRef, onReady, ghost }) {
  const canvasRef = useRef(null);
  const ghostRef = useRef(null);
  const stageRef = useRef(null);
  const docRef = useRef(null);
  const pageRef = useRef(null);
  const ghostDoc = useRef(null);
  const ghostPage = useRef(null);
  const ghostTask = useRef(null);
  const [changes, setChanges] = useState(null);
  const pageRotation = useRef(0);        // the sheet's OWN /Rotate — the uploaded orientation
  const drag = useRef(null);             // hand tool: drag the paper, never the page
  const [docTick, setDocTick] = useState(0);
  const [vp, setVp] = useState(null);
  const [frame, setFrame] = useState(null);       // sheet → css mapping: { s, u, w, h }
  const [gesture, setGesture] = useState(null);   // live pinch: { s, dx, dy, ox, oy }
  const pinch = useRef(null);
  const renderTask = useRef(null);
  const paintTimer = useRef(null);
  const lastZoom = useRef(zoom);
  const anchor = useRef(null);           // {cx, cy} in container-client px, or {keep:true}
  const lastTap = useRef(null);

  // One zoom model: wheel, +/- buttons, pinch and double-tap all go through this.
  const zoomTo = useCallback((next, clientX, clientY) => {
    const el = containerRef?.current;
    const z = clampZoom(next);
    if (!el || z === zoom) return;
    const r = el.getBoundingClientRect();
    anchor.current = clientX == null
      ? { cx: el.clientWidth / 2, cy: el.clientHeight / 2 }
      : { cx: clientX - r.left, cy: clientY - r.top };
    onZoom?.(z);
  }, [containerRef, onZoom, zoom]);

  // wheel = zoom at the cursor, and the browser page never scrolls while a sheet is open.
  // The listener has to be non-passive to preventDefault, so it is attached by hand.
  useEffect(() => {
    const el = containerRef?.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      if (!e.deltaY) return;
      // one notch per event, whatever the device claims: a trackpad fling sends a long tail of
      // shrinking deltas, and un-clamped they read as momentum that keeps zooming after the
      // fingers stop.
      const d = Math.max(-120, Math.min(120, e.deltaY));
      zoomTo(zoom * Math.exp(-d / 400), e.clientX, e.clientY);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [containerRef, zoom, zoomTo]);

  // the older revision: its own PDF.js document, rendered on a second canvas under the same viewport
  useEffect(() => {
    let alive = true;
    setChanges(null);
    if (!ghost?.pdfBytes) { ghostDoc.current = null; ghostPage.current = null; return undefined; }
    const task = pdfjsLib.getDocument({ data: ghost.pdfBytes.slice(0) });
    task.promise.then(async (doc) => {
      if (!alive) return;
      ghostDoc.current = doc;
      try { ghostPage.current = await doc.getPage(1); } catch { ghostPage.current = null; }
      if (!alive) return;
      setDocTick((t) => t + 1);
      try {
        const cells = await diffPages(pageRef.current, ghostPage.current);
        if (alive) { setChanges(cells); ghost.onChanges?.(cells.length); }
      } catch { if (alive) setChanges([]); }
    }).catch(() => {});
    return () => { alive = false; task.destroy?.(); ghostDoc.current = null; ghostPage.current = null; };
    // eslint-disable-next-line
  }, [ghost?.pdfBytes]);

  useEffect(() => {
    let alive = true;
    if (!pdfBytes) return undefined;
    const task = pdfjsLib.getDocument({ data: pdfBytes.slice(0) });
    task.promise.then(async (doc) => {
      if (!alive) return;
      docRef.current = doc;
      try { pageRef.current = await doc.getPage(1); } catch { pageRef.current = null; }
      if (alive) setDocTick((t) => t + 1);
    }).catch(() => {});
    return () => { alive = false; task.destroy?.(); docRef.current = null; pageRef.current = null; };
  }, [pdfBytes]);

  // refit when the container changes width (Compare splits the view, panels open, phone rotates).
  // The scroll box can never be widened by its own content (min-w-0 on every ancestor), so this
  // can not feed back into the fit calculation the way it used to.
  const [fitTick, setFitTick] = useState(0);
  useEffect(() => {
    const el = containerRef?.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    let last = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (Math.abs(el.clientWidth - last) > 4) { last = el.clientWidth; setFitTick((t) => t + 1); }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef]);

  // GEOMETRY — synchronous. Canvas CSS box, overlay viewport and the cursor anchor all land in the
  // same frame as the zoom change; the bitmap catches up a moment later.
  useLayoutEffect(() => {
    const page = pageRef.current;
    const el = containerRef?.current;
    const canvas = canvasRef.current;
    if (!page || !el || !canvas) return undefined;
    // ORIENTATION: the sheet renders in the orientation it was uploaded in — the page's own
    // /Rotate — plus whatever the user turned it by. Nothing here ever guesses landscape.
    pageRotation.current = page.rotate || 0;
    const rot = (((page.rotate || 0) + (rotation || 0)) % 360 + 360) % 360;
    const base = page.getViewport({ scale: 1, rotation: rot });
    // SHEET SPACE — the markup store holds PyMuPDF annotation coordinates, and PyMuPDF reports
    // those in the page's UN-ROTATED box with the origin top-left. The flip back into PDF user
    // space therefore has to use the un-rotated page height: the sheet row's `height` is the
    // ROTATED height, so on the 50 sheets in this set carrying /Rotate 90 or 270 every mark was
    // displaced by the difference between the two.
    const un = page.getViewport({ scale: 1, rotation: 0 });
    const fit = Math.max(0.05, (el.clientWidth - 8) / base.width);
    const viewport = page.getViewport({ scale: fit * zoom, rotation: rot });
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    setVp(viewport);
    const frameNow = { vp: viewport, sheetH: un.height, spin: rot };
    setFrame(frameNow);

    const prev = lastZoom.current;
    lastZoom.current = zoom;
    const a = anchor.current;
    anchor.current = null;
    if (zoom !== prev && !a?.keep) {
      const k = zoom / prev;
      const cx = a ? a.cx : el.clientWidth / 2;
      const cy = a ? a.cy : el.clientHeight / 2;
      el.scrollLeft = (el.scrollLeft + cx) * k - cx;
      el.scrollTop = (el.scrollTop + cy) * k - cy;
    }

    let cancelled = false;
    const paint = () => {
      // ZOOM RESOLUTION: render at the device pixel ratio, but never ask for a canvas bigger than
      // the browser will actually allocate — over the limit it silently downscales, which is what
      // made zoomed sheets go soft.
      const budget = 24e6;
      const raw = Math.min(window.devicePixelRatio || 1, 3);
      const area = viewport.width * viewport.height;
      const dpr = Math.max(1, Math.min(raw, Math.sqrt(budget / Math.max(area, 1))));
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
      const ctx = canvas.getContext("2d");
      renderTask.current?.cancel?.();
      const t = page.render({ canvasContext: ctx, viewport, transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null });
      renderTask.current = t;
      t.promise.then(() => { if (!cancelled) onReady?.(viewport, frameNow); }).catch(() => {});
      const gp = ghostPage.current, gc = ghostRef.current;
      if (gp && gc) {
        const grot = (((gp.rotate || 0) + (rotation || 0)) % 360 + 360) % 360;
        const gvp = gp.getViewport({ scale: fit * zoom, rotation: grot });
        gc.width = Math.floor(gvp.width * dpr); gc.height = Math.floor(gvp.height * dpr);
        gc.style.width = `${gvp.width}px`; gc.style.height = `${gvp.height}px`;
        const gctx = gc.getContext("2d", { willReadFrequently: true });
        ghostTask.current?.cancel?.();
        const gt = gp.render({ canvasContext: gctx, viewport: gvp, transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null });
        ghostTask.current = gt;
        gt.promise.then(() => { if (!cancelled) blueprint(gctx, gc.width, gc.height); }).catch(() => {});
      }
    };
    clearTimeout(paintTimer.current);
    // first paint of a sheet is immediate; a zoom change waits for the wheel to settle
    paintTimer.current = setTimeout(paint, vp ? 90 : 0);
    return () => { cancelled = true; clearTimeout(paintTimer.current); };
    // eslint-disable-next-line
  }, [zoom, rotation, docTick, height, fitTick]);

  // sheet space (x right, y down, origin top-left of the UN-rotated page) → css px
  const matrix = frame?.vp?.width ? (() => {
    const [a, b, c, d, e, f] = frame.vp.transform;
    const H = frame.sheetH;
    return [a, b, -c, -d, c * H + e, d * H + f];
  })() : null;
  const toSheet = (clientX, clientY) => {
    if (!canvasRef.current || typeof frame?.vp?.convertToPdfPoint !== "function") return null;   // page not laid out yet — an early tap is ignored, never a crash
    const r = canvasRef.current.getBoundingClientRect();
    const g = gesture?.s || 1;
    const [x, y] = frame.vp.convertToPdfPoint((clientX - r.left) / g, (clientY - r.top) / g);
    return [Math.round(x * 100) / 100, Math.round((frame.sheetH - y) * 100) / 100];
  };
  const fire = (phase, e) => { const pt = toSheet(e.clientX, e.clientY); if (pt) onPointer?.(phase, pt, e); };

  const active = useRef(new Map());
  const pt2 = () => [...active.current.values()];
  const startPinch = () => {
    const el = containerRef?.current;
    const stage = stageRef.current;
    if (!el || !stage) return;
    const [p1, p2] = pt2();
    const r = el.getBoundingClientRect();
    const sr = stage.getBoundingClientRect();
    const mid = { x: (p1.clientX + p2.clientX) / 2, y: (p1.clientY + p2.clientY) / 2 };
    pinch.current = {
      d: Math.max(1, Math.hypot(p1.clientX - p2.clientX, p1.clientY - p2.clientY)),
      // the paper point under the fingers, in scroll-content px, and where that sits on screen
      content: { x: el.scrollLeft + mid.x - r.left, y: el.scrollTop + mid.y - r.top },
      origin: { x: mid.x - sr.left, y: mid.y - sr.top },     // stage-local, unscaled → CSS origin
      mid,
      midC: { x: mid.x - r.left, y: mid.y - r.top },
      s: 1, dx: 0, dy: 0,
    };
    setGesture({ s: 1, dx: 0, dy: 0, ox: pinch.current.origin.x, oy: pinch.current.origin.y });
    onPointer?.("cancel");
  };
  const endPinch = () => {
    const g = pinch.current;
    const el = containerRef?.current;
    const stage = stageRef.current;
    pinch.current = null;
    setGesture(null);
    if (!g || !el || !stage) return;
    const next = clampZoom(zoom * g.s);
    const k = next / zoom;                                  // what the paper actually grows by
    const off = { x: stage.offsetLeft, y: stage.offsetTop }; // the scroller's own padding
    anchor.current = { keep: true };                        // the scroll is set here, not by the fit
    if (Math.abs(g.s - 1) > 0.01) onZoom?.(next);
    // the paper under the fingers stays under the fingers, two-finger drag included
    const left = (g.content.x - off.x) * k + off.x - g.midC.x;
    const top = (g.content.y - off.y) * k + off.y - g.midC.y;
    requestAnimationFrame(() => { el.scrollLeft = left; el.scrollTop = top; });
  };

  const down = (e) => {
    active.current.set(e.pointerId, e);
    if (active.current.size === 2) { drag.current = null; startPinch(); return; }
    if (!vp || active.current.size > 2) return;
    const drawing = tool !== "pan" || e.pointerType === "pen";
    if (!drawing) {
      // hand tool: drag the paper inside its own scroller, mouse or finger alike.
      // NO pointer capture yet — capture retargets the browser's click to this div, and a tap on
      // the paper has to reach the markup underneath it. Capture is taken on the first real move.
      const el = containerRef?.current;
      if (el) {
        drag.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop, moved: 0, raf: 0, nx: el.scrollLeft, ny: el.scrollTop, held: false };
        if (stageRef.current) stageRef.current.style.cursor = e.pointerType === "mouse" ? "grabbing" : "";
      }
      return;
    }
    // a pointer that has already gone (or a synthetic one from a test harness) throws here, and
    // an exception would take the rest of the gesture with it
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* not capturable */ }
    fire("down", e);
  };
  const move = (e) => {
    if (active.current.has(e.pointerId)) active.current.set(e.pointerId, e);
    if (pinch.current && active.current.size >= 2) {
      const [p1, p2] = pt2();
      const d = Math.max(1, Math.hypot(p1.clientX - p2.clientX, p1.clientY - p2.clientY));
      const mid = { x: (p1.clientX + p2.clientX) / 2, y: (p1.clientY + p2.clientY) / 2 };
      const s = Math.max(0.2, Math.min(6, d / pinch.current.d));
      const r = containerRef?.current?.getBoundingClientRect();
      pinch.current.s = s;
      pinch.current.dx = mid.x - pinch.current.mid.x;
      pinch.current.dy = mid.y - pinch.current.mid.y;
      if (r) pinch.current.midC = { x: mid.x - r.left, y: mid.y - r.top };
      setGesture({ s, dx: pinch.current.dx, dy: pinch.current.dy, ox: pinch.current.origin.x, oy: pinch.current.origin.y });
      return;
    }
    const d = drag.current;
    if (d) {
      // one scroll write per frame: a 100-sheet set at 400% is a big paint, and two writes in a
      // frame is what made the drag feel like the screen had locked up
      const el = containerRef?.current;
      if (!el) return;
      d.moved = Math.max(d.moved, Math.hypot(e.clientX - d.x, e.clientY - d.y));
      if (d.moved > 4 && !d.held) {
        // it really is a drag now — take the pointer so it keeps coming when the paper slides out
        // from under the cursor. A tap never gets here, so a tap still clicks the markup.
        d.held = true;
        try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* not capturable */ }
      }
      d.nx = d.left - (e.clientX - d.x);
      d.ny = d.top - (e.clientY - d.y);
      if (!d.raf) {
        d.raf = requestAnimationFrame(() => {
          d.raf = 0;
          el.scrollLeft = d.nx;
          el.scrollTop = d.ny;
        });
      }
      return;
    }
    if (!vp || !active.current.has(e.pointerId)) return;
    if (tool === "pan" && e.pointerType !== "pen") return;
    fire("move", e);
  };
  const up = (e) => {
    const had = active.current.has(e.pointerId);
    active.current.delete(e.pointerId);
    if (pinch.current) { if (active.current.size < 2) endPinch(); return; }
    const d = drag.current;
    if (d) {
      if (d.raf) cancelAnimationFrame(d.raf);
      drag.current = null;
      if (stageRef.current) stageRef.current.style.cursor = "";
      // a tap, not a drag: double-tap zooms in at the tap point, and back out to fit
      if (d.moved < 6 && e.pointerType !== "mouse") {
        const t = lastTap.current;
        const now = Date.now();
        if (t && now - t.t < 320 && Math.hypot(e.clientX - t.x, e.clientY - t.y) < 40) {
          lastTap.current = null;
          zoomTo(zoom >= 2 ? 1 : zoom * 2, e.clientX, e.clientY);
        } else lastTap.current = { t: now, x: e.clientX, y: e.clientY };
      }
      return;
    }
    if (!vp || !had) return;
    if (tool === "pan" && e.pointerType !== "pen") return;
    fire("up", e);
  };

  return (
    <div ref={stageRef} className="relative inline-block select-none"
         style={{ transform: gesture ? `translate(${gesture.dx}px, ${gesture.dy}px) scale(${gesture.s})` : undefined,
                  transformOrigin: gesture ? `${gesture.ox}px ${gesture.oy}px` : "0 0",
                  willChange: gesture ? "transform" : undefined,
                  cursor: tool === "pan" ? "grab" : "crosshair",
                  touchAction: "none" }}
         onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
         data-testid="pdf-stage">
      <canvas ref={canvasRef} className="block bg-white shadow-md" data-testid="sheet-canvas" />
      {ghost?.pdfBytes && (
        <canvas ref={ghostRef} className="absolute left-0 top-0 pointer-events-none" data-testid="ghost-canvas"
                style={{ opacity: ghost.visible ? ghost.opacity : 0 }} />
      )}
      {matrix && (
        <svg className="absolute inset-0" width={vp.width} height={vp.height}
             style={{ pointerEvents: tool === "pan" ? "auto" : "none" }} data-testid="markup-overlay">
          <g transform={`matrix(${matrix.join(" ")})`}>
            {ghost?.showChanges && changes && (
              <g pointerEvents="none" data-testid="change-tint" data-count={changes.length}>
                {changes.map(([x, y, w, h], i) => <rect key={i} x={x} y={y} width={w} height={h} fill="rgba(249,115,22,0.22)" stroke="rgba(234,88,12,0.7)" strokeWidth={0.6} vectorEffect="non-scaling-stroke" />)}
              </g>
            )}
            {typeof children === "function" ? children({ scale: vp.scale, spin: frame?.spin || 0, toSheet }) : children}
          </g>
        </svg>
      )}
    </div>
  );
}
